/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logVisualizationInteraction } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { numberToPercentageString } from 'src/core/utils/number';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { DetectedLanguagesDocuments, NamedEntity } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import {
    EntityCategory,
    SortTypes,
    getEntitiesSortOptions,
    getEntityColor,
    getEntityId,
    getFilterCategories,
    sortEntitiesHelper
} from 'src/languageStudio/core/utils/demosUtils';
import { ModelType } from 'src/luis/types/ModelType';
import { Label } from 'src/modules/labeling/types/Label';
import { calculateRenderingLevel } from 'src/modules/renderingUtils/renderingUtils';
import { Labeler } from 'src/packages/react-text-annotator/lib/esm';

type ResultToken = { id: string; tokens: { startTokenIndex: number; endTokenIndex: number } };
export type VisualizedNERResultProps<T> = {
    document: string;
    featureName?: TelemetryFeatureTitle;
    isLabelerRTL: boolean;
    redactedDocument?: string;
    entities: NamedEntity<T>[];
    entitiesSectionTitle: string;
    hiddenEntitiesMessage: string;
    showRedactionToggle?: boolean;
    isLabelerWordTokenized: boolean;
    defaultCardsHiddenState?: boolean;
    detectedLanguage?: DetectedLanguagesDocuments;
};

const messages = defineMessages({
    sort: { id: 'languageStudio.visualizedNERResult.sort', defaultMessage: 'Sort' },
    filter: { id: 'languageStudio.visualizedNERResult.filter', defaultMessage: 'Filter' },
    hidePiiLabel: { id: 'languageStudio.visualizedNERResult.hidePiiLabel', defaultMessage: 'Hide PII' },
    showCardsLabel: { id: 'languageStudio.visualizedNERResult.showCardsLabel', defaultMessage: 'Show cards' },
    entityValueLabel: { id: 'languageStudio.visualizedNERResult.entityValueLabel', defaultMessage: 'Entity value:' },
    moreItemsAriaLabel: { id: 'languageStudio.visualizedNERResult.moreItemsAriaLabel', defaultMessage: 'More items' }
});

const theme = getScopedTheme('DemoPage');
const contextualMenuButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ menuIcon: { display: 'none' } });
const entityCardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { width: '200px', minHeight: '110px' } });

export const getLabel = (entity: NamedEntity<EntityCategory | string>, tokens: ResultToken[]): Label => {
    const id = getEntityId(entity);
    const eTokens = tokens.find(t => t.id === id).tokens;

    return {
        depth: 1,
        renderingLevel: 0,
        renderedAs: 'line',
        end: eTokens.endTokenIndex,
        start: eTokens.startTokenIndex,
        intrinsicEnd: eTokens.endTokenIndex,
        intrinsicStart: eTokens.startTokenIndex,
        entity: { id, name: entity.category, roles: [], typeId: ModelType.Simple }
    };
};

export const VisualizedNERResult = <T extends string>(props: VisualizedNERResultProps<T>) => {
    const {
        entities,
        document,
        featureName,
        isLabelerRTL,
        detectedLanguage,
        redactedDocument,
        entitiesSectionTitle,
        hiddenEntitiesMessage,
        isLabelerWordTokenized,
        showRedactionToggle = false,
        defaultCardsHiddenState = false
    } = props;
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();

    const [showRedactedText, setShowRedactedText] = React.useState(false);
    const [showCards, setShowCards] = React.useState(!defaultCardsHiddenState);
    const [filteredCategories, setFilteredCategories] = React.useState<EntityCategory[]>([]);
    const [selectedSortType, setSelectedSortType] = React.useState<SortTypes>('appearanceAsc');
    const filterButtonRef = React.useRef(null);

    const filtersMenuItems: IContextualMenuItem[] = React.useMemo(
        () =>
            getFilterCategories<NamedEntity<T>>(entities, 'category', filteredCategories, (newCategories: EntityCategory[]) =>
                setFilteredCategories(newCategories)
            ),
        [entities, filteredCategories]
    );
    const sortMenuItems: IContextualMenuItem[] = getEntitiesSortOptions(selectedSortType, setSelectedSortType);
    const filterText = !filteredCategories.length
        ? localize(messages.filter)
        : `${localize(messages.filter)} (${filteredCategories.length})`;
    const controllers: ICommandBarItemProps[] = [
        {
            key: 'showCards',
            onRender: () => (
                <Stack role="menuitem">
                    <Toggle
                        checked={showCards}
                        inlineLabel={isXLargeOrGreater}
                        styles={{ root: { marginBottom: 0, height: '100%' } }}
                        label={localize(messages.showCardsLabel)}
                        onText={localize(commonDemosMessages.onText)}
                        offText={localize(commonDemosMessages.offText)}
                        onChange={(_, checked) => {
                            setShowCards(checked);
                            handleVisualizationInteraction('ShowCards');
                        }}
                    />
                </Stack>
            )
        },
        {
            key: 'sort',
            onRender: () => (
                <Stack role="menuitem">
                    <ActionButton
                        disabled={!showCards}
                        text={localize(messages.sort)}
                        menuProps={{ items: sortMenuItems }}
                        iconProps={{ iconName: 'Ascending' }}
                    />
                </Stack>
            )
        },
        {
            key: 'filter',
            onRender: () => (
                <Stack role="menuitem">
                    <ActionButton
                        componentRef={filterButtonRef}
                        text={filterText}
                        disabled={!showCards}
                        iconProps={{ iconName: 'Filter' }}
                        styles={contextualMenuButtonStyles}
                        menuProps={{
                            items: filtersMenuItems,
                            onMenuDismissed: () => {
                                if (filterButtonRef.current) {
                                    filterButtonRef.current.focus();
                                }
                            }
                        }}
                    />
                </Stack>
            )
        }
    ];

    if (showRedactionToggle) {
        controllers.splice(0, 0, {
            key: 'redactDocument',
            onRender: () => (
                <Stack role="menuitem">
                    <Toggle
                        checked={showRedactedText}
                        inlineLabel={isXLargeOrGreater}
                        styles={{ root: { marginBottom: 0 } }}
                        label={localize(messages.hidePiiLabel)}
                        onText={localize(commonDemosMessages.onText)}
                        offText={localize(commonDemosMessages.offText)}
                        onChange={(_, checked) => setShowRedactedText(checked)}
                    />
                </Stack>
            )
        });
    }

    const entityCard = (entity: NamedEntity<T>) => (
        <Stack
            key={getEntityId(entity)}
            tokens={{ childrenGap: 7 }}
            data-automation-id={getEntityId(entity)}
            styles={mergeStyleSets(resultCardStyles, entityCardStyles)}>
            {entity.subcategory ? (
                <Stack tokens={{ childrenGap: 3 }}>
                    <LuisText fontWeight={500}>{entity.category}</LuisText>
                    <LuisText variant="medium" styles={resultCardTitleStyles(getEntityColor<T>(entity.category))}>
                        {entity.subcategory}
                    </LuisText>
                </Stack>
            ) : (
                <LuisText fontWeight={500} styles={resultCardTitleStyles(getEntityColor<T>(entity.category))}>
                    {entity.category}
                </LuisText>
            )}
            <Stack>
                <LuisText variant="smallPlus">
                    {localize(messages.entityValueLabel)} {entity.text}
                </LuisText>
                <LuisText variant="smallPlus">
                    {localize(commonDemosMessages.confidenceLabel)} {numberToPercentageString({ ratio: entity.confidenceScore })}
                </LuisText>
            </Stack>
        </Stack>
    );

    const resultTokens = entities.map(e => ({
        id: getEntityId(e),
        tokens: { startTokenIndex: e.offset, endTokenIndex: e.offset + e.length - 1 }
    }));
    const entityAnnotations = calculateRenderingLevel(
        entities
            .map(e => getLabel(e, resultTokens))
            .map(label =>
                transformLabelToAnnotation(label, entityCard(entities.find(e => label.entity.id === getEntityId(e))), l =>
                    getEntityColor(l.entity.name as EntityCategory)
                )
            )
    );
    const handleVisualizationInteraction = (actionName: string) => {
        logVisualizationInteraction(featureName, actionName);
    };

    return (
        <Stack styles={{ root: { width: '100%' } }} verticalAlign="end" tokens={{ childrenGap: 25 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <LuisText as="h3" fontWeight={500}>
                    {entitiesSectionTitle}
                </LuisText>

                <LuisCommandBar
                    showTopBorder={false}
                    onReduceData={() => undefined}
                    items={isXLargeOrGreater ? controllers : []}
                    overflowItems={!isXLargeOrGreater && controllers}
                />
            </Stack>

            <Stack
                wrap
                horizontal
                tokens={{ childrenGap: 20 }}
                data-automation-id="entityCardsContainer"
                styles={{ root: { marginTop: !showCards && '15px !important' } }}>
                {showCards ? (
                    entities.length ? (
                        entities
                            .filter(e => filteredCategories.length === 0 || filteredCategories.some(c => c === e.category))
                            .sort((a, b) => sortEntitiesHelper<T>(a, b, selectedSortType))
                            .map(entityCard)
                    ) : (
                        <LuisText styles={{ root: { margin: 'auto' } }}>{localize(commonDemosMessages.noEntitiesFound)}</LuisText>
                    )
                ) : (
                    <LuisText data-automation-id="hiddenCardsMessage" color={theme.secondaryTextColor}>
                        {hiddenEntitiesMessage}
                    </LuisText>
                )}
            </Stack>

            <LanguageCard language={detectedLanguage?.documents[0].detectedLanguage} />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(commonDemosMessages.originalText)}
                </LuisText>
                <Labeler
                    annotations={entityAnnotations}
                    text={showRedactedText ? redactedDocument : document}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                />
            </Stack>
        </Stack>
    );
};
